import React, { useState, useEffect } from 'react';

const API_URL = '';

export default function FilesPanel() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      // FIX: Added /api/ prefix
      const response = await fetch(`${API_URL}/api/admin/dashboard/downloads`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📦 API RESPONSE:', data);
      setFiles(data.files || []);
      setLoading(false);
    } catch (error) {
      console.error('💥 FULL ERROR:', error);
      setFiles([]);
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;

    setUploading(true);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => formData.append('files', file));

      const token = localStorage.getItem('token');
      // FIX: Added /api/ prefix
      const response = await fetch(`${API_URL}/api/admin/dashboard/downloads/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully uploaded ${data.files?.length || selectedFiles.length} file(s)`);
        fetchFiles(); // Refresh the list
      } else {
        alert('Upload failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;

    try {
      const token = localStorage.getItem('token');
      // FIX: Added /api/ prefix
      const response = await fetch(`${API_URL}/api/admin/dashboard/downloads`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ filename })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('File deleted successfully');
        fetchFiles(); // Refresh the list
      } else {
        alert('Delete failed: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert('Delete failed: ' + error.message);
    }
  };

  const copyPath = (path) => {
    if (!path) {
      alert('No path to copy');
      return;
    }
    
    navigator.clipboard.writeText(path);
    alert(`Copied: ${path}`);
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (filename) => {
    if (!filename) return '📁';
    if (filename.endsWith('.pdf')) return '📄';
    if (filename.endsWith('.zip')) return '📦';
    if (filename.endsWith('.epub')) return '📖';
    if (filename.endsWith('.mp3')) return '🎵';
    if (filename.endsWith('.mp4')) return '🎬';
    if (filename.endsWith('.jpg') || filename.endsWith('.png')) return '🖼️';
    return '📁';
  };

  useEffect(() => {
    fetchFiles();
  }, []);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>Loading files...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Download Files Manager</h2>
        <p style={styles.subtitle}>Manage downloadable files for your products</p>
      </div>

      <div style={styles.uploadSection}>
        <label style={styles.uploadBtn}>
          {uploading ? 'Uploading...' : '📤 Upload Files'}
          <input
            type="file"
            multiple
            accept=".pdf,.zip,.epub,.mp3,.mp4,.jpg,.png"
            onChange={handleUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>
        <p style={styles.uploadHint}>Supports: PDF, ZIP, EPUB, MP3, MP4, JPG, PNG</p>
      </div>

      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>{files.length}</span>
          <span style={styles.statLabel}>Total Files</span>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statNumber}>
            {files.reduce((sum, f) => sum + (f.size || 0), 0)} MB
          </span>
          <span style={styles.statLabel}>Total Size</span>
        </div>
      </div>

      <div style={styles.filesList}>
        {files.map((file, index) => (
          <div key={file.filename || index} style={styles.fileCard}>
            <div style={styles.fileIcon}>{getFileIcon(file.filename)}</div>
            <div style={styles.fileInfo}>
              <div style={styles.fileName}>{file.filename || 'Unknown'}</div>
              <div style={styles.fileMeta}>
                <span>{formatFileSize(file.size)}</span>
                <span>•</span>
                <span>{file.created ? new Date(file.created).toLocaleDateString() : 'Unknown date'}</span>
              </div>
              <div style={styles.filePath}>{file.path || '/uploads/downloads/...'}</div>
            </div>
            <div style={styles.fileActions}>
              <button onClick={() => copyPath(file.path)} style={styles.copyBtn}>
                📋 Copy Path
              </button>
              <button onClick={() => handleDelete(file.filename)} style={styles.deleteBtn}>
                🗑️ Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {files.length === 0 && (
        <div style={styles.empty}>
          <span style={styles.emptyIcon}>📁</span>
          <p>No files uploaded yet</p>
          <p style={styles.emptySubtext}>Click "Upload Files" to add downloadable content</p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '30px',
    backgroundColor: '#0a0a0a',
    minHeight: '100vh',
    color: '#fff'
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    backgroundColor: '#0a0a0a',
    color: '#fff'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(206, 0, 33, 0.1)',
    borderTop: '4px solid #ce0021',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#ce0021',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#999',
    margin: 0
  },
  uploadSection: {
    marginBottom: '30px'
  },
  uploadBtn: {
    display: 'inline-block',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #ce0021 0%, #ff6b35 100%)',
    color: '#fff',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    border: 'none',
    transition: 'transform 0.2s'
  },
  uploadHint: {
    fontSize: '12px',
    color: '#666',
    marginTop: '8px'
  },
  stats: {
    display: 'flex',
    gap: '16px',
    marginBottom: '30px'
  },
  statCard: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    padding: '16px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px'
  },
  statNumber: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#ce0021'
  },
  statLabel: {
    fontSize: '12px',
    color: '#999'
  },
  filesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    transition: 'all 0.3s'
  },
  fileIcon: {
    fontSize: '32px'
  },
  fileInfo: {
    flex: 1
  },
  fileName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    marginBottom: '4px'
  },
  fileMeta: {
    fontSize: '12px',
    color: '#999',
    display: 'flex',
    gap: '8px',
    marginBottom: '4px'
  },
  filePath: {
    fontSize: '11px',
    color: '#666',
    fontFamily: 'monospace'
  },
  fileActions: {
    display: 'flex',
    gap: '8px'
  },
  copyBtn: {
    padding: '8px 12px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    color: '#fff',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'all 0.2s'
  },
  deleteBtn: {
    padding: '8px 12px',
    background: 'rgba(206, 0, 33, 0.2)',
    border: '1px solid rgba(206, 0, 33, 0.3)',
    borderRadius: '6px',
    color: '#ce0021',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'all 0.2s'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'rgba(255, 255, 255, 0.02)',
    borderRadius: '16px',
    border: '1px dashed rgba(255, 255, 255, 0.1)'
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px'
  },
  emptySubtext: {
    fontSize: '12px',
    color: '#666',
    marginTop: '8px'
  }
};

// Add keyframes for spinner
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);